import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Flex,
  Tag,
  Icon,
} from '@chakra-ui/react';
import { LuMessageCircle } from 'react-icons/lu';
import { FaInfoCircle } from 'react-icons/fa';
import Prism from 'prismjs';
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css";

const WhatsAppErrorModal = ({ 
  isOpen, 
  onClose, 
  selectedTemplate 
}) => {
  if (!selectedTemplate) return null;

  const errorData = selectedTemplate.whatsapp_error 
    ? (typeof selectedTemplate.whatsapp_error === 'string' 
        ? JSON.parse(selectedTemplate.whatsapp_error) 
        : selectedTemplate.whatsapp_error)
    : null;

  const error = errorData?.error;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={LuMessageCircle} color="red.500" boxSize={5} />
            <Text>WhatsApp Template Error Details</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Template Information */}
            <Box
              p={4}
              bg="#fef2f2"
              border="1px solid"
              borderColor="#fecaca"
              borderRadius="md"
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="#991b1b">
                    Template Information
                  </Text>
                  <Text fontSize="xs" color="#dc2626" mt={1}>
                    Name: {selectedTemplate.name}
                  </Text>
                  <Text fontSize="xs" color="#dc2626">
                    Template ID: {selectedTemplate.id}
                  </Text>
                </Box>
                <Tag bg="#fee2e2" color="#991b1b" borderColor="#fecaca" border="1px solid" size="sm">
                  Error
                </Tag>
              </Flex>
            </Box>

            {/* Error Details */}
            {error ? (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                  Error Details
                </Text>
                
                <VStack align="stretch" spacing={3}>
                  {/* Error Title */}
                  {error.error_user_title && (
                    <Box
                      p={3}
                      bg="red.50"
                      border="1px solid"
                      borderColor="red.200"
                      borderRadius="md"
                    >
                      <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                        Error Title
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="red.700">
                        {error.error_user_title}
                      </Text>
                    </Box>
                  )}

                  {/* Error Message */}
                  {error.error_user_msg && (
                    <Box
                      p={3}
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.200"
                      borderRadius="md"
                    >
                      <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                        User Message
                      </Text>
                      <Text fontSize="sm" color="orange.900">
                        {error.error_user_msg}
                      </Text>
                    </Box>
                  )}

                

                  {/* Full JSON Response */}
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                      Full Error Response
                    </Text>
                    <pre
                      style={{
                        backgroundColor: "black",
                        textShadow: "none",
                        color: "white",
                        padding: "0px",
                        borderRadius: "8px",
                        fontFamily: "monospace",
                        fontSize: "12px",
                      }}
                    >
                      <span
                        style={{
                          padding: 15,
                          backgroundColor: "#2F2F2F",
                          fontSize: "12px",
                          display: "block",
                          marginBottom: "10px",
                        }}
                      >
                        json
                      </span>

                      <code
                        className="language-json"
                        style={{ 
                          padding: "0px 15px 15px", 
                          display: "block", 
                          whiteSpace: "pre-wrap",
                          fontSize: "12px",
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: Prism.highlight(
                            JSON.stringify(errorData, null, 2), 
                            Prism.languages.json, 
                            'json'
                          ) 
                        }}
                      />
                    </pre>
                  </Box>
                </VStack>
              </Box>
            ) : (
              <Flex
                direction="column"
                align="center"
                justify="center"
                minH="150px"
                color="gray.500"
              >
                <Icon as={FaInfoCircle} boxSize={12} opacity={0.3} mb={2} />
                <Text fontSize="sm">No error details available</Text>
              </Flex>
            )}

            {/* Info Alert */}
            <Box
              p={3}
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius="md"
            >
              <HStack spacing={2} align="start">
                <Icon as={FaInfoCircle} color="blue.500" boxSize={4} mt={0.5} />
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" fontWeight="medium" color="blue.900">
                    How to resolve this error:
                  </Text>
                  <Text fontSize="xs" color="blue.800">
                    1. Review the error message and adjust your template accordingly
                  </Text>
                  
                  <Text fontSize="xs" color="blue.800">
                    2. Check WhatsApp Business API guidelines for template formatting
                  </Text>
                  <Text fontSize="xs" color="blue.800">
                    3. Re-submit the template after making corrections
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={onClose}
            size="sm"
            bg="var(--brand-primary)"
            color="white"
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WhatsAppErrorModal;